/**
 * React Query key factory.
 *
 * Keys are built here rather than inline so that an invalidation can target a
 * whole resource (`queryKeys.products.all`) without guessing at the shape of
 * the key a hook happened to use.
 */
export const queryKeys = {
  dashboard: ['dashboard'] as const,
  settings: {
    all: ['settings'] as const,
    detail: (key: string) => ['settings', 'detail', key] as const,
  },
  /**
   * The workspace's country. Its own key rather than a `settings` detail,
   * because it is not a stored document: it is derived from the country pack
   * the workspace was provisioned with.
   */
  country: ['settings', 'country'] as const,

  products: {
    all: ['products'] as const,
    list: (params: unknown) => ['products', 'list', params] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    stock: (id: string) => ['products', 'stock', id] as const,
  },

  categories: {
    all: ['categories'] as const,
    list: () => ['categories', 'list'] as const,
    tree: () => ['categories', 'tree'] as const,
  },

  units: {
    all: ['units'] as const,
    list: (params: unknown) => ['units', 'list', params] as const,
  },

  warehouses: {
    all: ['warehouses'] as const,
    list: (params: unknown) => ['warehouses', 'list', params] as const,
  },

  stock: {
    all: ['stock'] as const,
    list: (params: unknown) => ['stock', 'list', params] as const,
    valuation: () => ['stock', 'valuation'] as const,
  },

  movements: {
    all: ['movements'] as const,
    list: (params: unknown) => ['movements', 'list', params] as const,
  },

  documents: {
    all: ['documents'] as const,
    list: (params: unknown) => ['documents', 'list', params] as const,
    detail: (id: string) => ['documents', 'detail', id] as const,
    preview: (id: string) => ['documents', 'preview', id] as const,
    summary: () => ['documents', 'summary'] as const,
  },

  counterparties: {
    all: ['counterparties'] as const,
    list: (params: unknown) => ['counterparties', 'list', params] as const,
  },

  users: {
    all: ['users'] as const,
    list: (params: unknown) => ['users', 'list', params] as const,
  },
} as const;
